import type { Element, Root } from 'hast';
import { visit } from 'unist-util-visit';
import { pathForResult, resolveRecordFromPath } from 'utils/globalSearchRoutes';
import { isCanonicalNumericRouteId } from 'utils/linkUtil';

export const AI_CHAT_CUSTOM_PROTOCOL = 'crespect';

export const AI_CHAT_CUSTOM_PROTOCOL_PREFIX = `${AI_CHAT_CUSTOM_PROTOCOL}://`;
const AI_CHAT_SAFE_ROUTE_SEGMENT_PATTERN = /^[A-Za-z0-9_-]+$/;
const AI_CHAT_RECORD_ROUTE_SEGMENTS = new Set([
    'matter',
    'contact',
    'employee',
    'invoice',
    'timecards',
    'effort',
    'group',
    'experience-records',
]);
// Record roots whose bare path is also a list page of its own, so the segment alone is a link too.
const AI_CHAT_LIST_ROUTE_SEGMENTS = new Set(['timecards', 'experience-records']);

export type AiChatLinkResolution =
    | {
          kind: 'internal';
          to: string;
      }
    | {
          kind: 'record';
          to: string;
      }
    | {
          kind: 'phone';
          href: string;
      }
    | {
          kind: 'email';
          href: string;
      }
    | {
          kind: 'external';
          href: string;
      }
    | {
          kind: 'plainText';
      };

const isSafePhoneHref = (href: string) => /^tel:\+?[0-9().\-\s]+$/i.test(href);

/**
 * Whether the href is one of this app's own record pages — the one link shape a message can carry
 * that opens a record in a new tab, the custom scheme opening in the same tab and an absolute URL
 * dragging in the leaving-this-site prompt.
 *
 * A round trip, not a scan: asking whether the href is *exactly* what {@link pathForResult} would
 * write for the record {@link resolveRecordFromPath} finds in it rejects a host, a query, a
 * fragment, a traversal, an encoding and a stripped control character at once. A scan passed
 * `/invoice/1187/../../admin`.
 */
export const isAiChatRecordPath = (href: string): boolean => {
    const record = resolveRecordFromPath(href);
    return record !== null && href === pathForResult(record.entityType, record.id);
};
const isSafeMailtoHref = (href: string) => /^mailto:[^?\s@]+@[^?\s@]+\.[^?\s@]+(?:\?.*)?$/i.test(href);

export const isSafeExternalHref = (href: string): boolean => {
    let parsedUrl: URL;

    try {
        parsedUrl = new URL(href);
    } catch {
        return false;
    }

    return parsedUrl.protocol === 'https:' && !parsedUrl.username && !parsedUrl.password;
};

const resolveAiChatInternalPath = (rawHref: string): string | null => {
    if (!rawHref.toLowerCase().startsWith(AI_CHAT_CUSTOM_PROTOCOL_PREFIX)) {
        return null;
    }

    // A menu path keeps its leading slash when the model pastes it after the scheme, which leaves the
    // host empty: `crespect:///timecards` is `crespect://timecards`. Only that one slash is dropped, and
    // the result goes through every check below like any other link.
    const href =
        rawHref[AI_CHAT_CUSTOM_PROTOCOL_PREFIX.length] === '/'
            ? AI_CHAT_CUSTOM_PROTOCOL_PREFIX + rawHref.slice(AI_CHAT_CUSTOM_PROTOCOL_PREFIX.length + 1)
            : rawHref;

    const rawInternalTarget = href.slice(AI_CHAT_CUSTOM_PROTOCOL_PREFIX.length);
    let decodedInternalTarget: string;

    try {
        decodedInternalTarget = decodeURIComponent(rawInternalTarget);
    } catch {
        return null;
    }

    // The https parse below reads a backslash as a slash, so `matter\..\admin` would walk past this check.
    if (/(^|\/)\.\.?($|\/)/.test(decodedInternalTarget) || decodedInternalTarget.includes('\\')) {
        return null;
    }

    let parsedUrl: URL;

    // The target is parsed as the path of a fixed https URL, never under its own scheme: engines disagree
    // on whether a non-special scheme has a host (Chromium before 130 reads none, leaving the whole target
    // in the path). A user, a port or a stray host lands in a path segment, which the pattern refuses.
    try {
        parsedUrl = new URL(`https://portal.invalid/${rawInternalTarget}`);
    } catch {
        return null;
    }

    const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);

    if (
        rawInternalTarget.startsWith('/') ||
        parsedUrl.search ||
        (parsedUrl.hash && pathSegments[0] !== 'help-center') ||
        pathSegments.length === 0 ||
        pathSegments.some((segment) => !AI_CHAT_SAFE_ROUTE_SEGMENT_PATTERN.test(segment))
    ) {
        return null;
    }

    const isListRoute = pathSegments.length === 1 && AI_CHAT_LIST_ROUTE_SEGMENTS.has(pathSegments[0]);

    if (
        AI_CHAT_RECORD_ROUTE_SEGMENTS.has(pathSegments[0]) &&
        !isListRoute &&
        (pathSegments.length < 2 ||
            pathSegments.length % 2 !== 0 ||
            pathSegments.some((segment, index) => index % 2 !== 0 && !isCanonicalNumericRouteId(segment)))
    ) {
        return null;
    }

    return `/${pathSegments.join('/')}${parsedUrl.hash}`;
};

export const resolveAiChatLink = (href?: string | null): AiChatLinkResolution => {
    const normalizedHref = href?.trim();

    if (!normalizedHref) {
        return { kind: 'plainText' };
    }

    if (isSafePhoneHref(normalizedHref)) {
        return {
            kind: 'phone',
            href: normalizedHref,
        };
    }

    if (isSafeMailtoHref(normalizedHref)) {
        return {
            kind: 'email',
            href: normalizedHref,
        };
    }

    const internalPath = resolveAiChatInternalPath(normalizedHref);

    if (internalPath) {
        return {
            kind: 'internal',
            to: internalPath,
        };
    }

    if (isAiChatRecordPath(normalizedHref)) {
        return {
            kind: 'record',
            to: normalizedHref,
        };
    }

    if (isSafeExternalHref(normalizedHref)) {
        return {
            kind: 'external',
            href: normalizedHref,
        };
    }

    return { kind: 'plainText' };
};

const isAnchorElement = (node: unknown): node is Element =>
    !!node &&
    typeof node === 'object' &&
    'type' in node &&
    'tagName' in node &&
    (node as Element).type === 'element' &&
    (node as Element).tagName === 'a';

export const unwrapUnsupportedAiChatLinks = () => (tree: Root) => {
    visit(tree, 'element', (node) => {
        if (!isAnchorElement(node)) {
            return;
        }

        const href = typeof node.properties?.href === 'string' ? node.properties.href : undefined;

        if (resolveAiChatLink(href).kind !== 'plainText') {
            return;
        }

        node.tagName = 'span';
        delete node.properties.href;
        delete node.properties.target;
        delete node.properties.rel;
    });
};
